import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Plus, Activity, Ruler, Brain, Heart, BarChart3, User, FolderOpen, Bell, ChevronLeft, ChevronRight, ExternalLink, Video, X, Maximize2, Minimize2, Utensils, ClipboardCheck, FileText, ClipboardEdit } from 'lucide-react';
import { toast } from 'sonner';
import ProfilePhotoUpload from '../components/ProfilePhotoUpload';
import InlineProfileEditor from '../components/InlineProfileEditor';
import TeamSelection from '../components/TeamSelection';
import PlayerTargets from '../components/PlayerTargets';
import ConsentStatus from '../components/ConsentStatus';
import AthleticAnalytics from '../components/AthleticAnalytics';
import UpcomingBookings from '../components/UpcomingBookings';
import CoachSummaryEditor from '../components/CoachSummaryEditor';
import useSwipe from '../hooks/useSwipe';
// Refactored Tab Components
import { MedicalTab, AICoachTab, NutritionTab, TrendsTab, MediaTab, AlertsTab, OverviewTab, MeasurementsTab } from '../components/tabs';
import ProfilingTab from '../components/ProfilingTab';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

// Tab configuration
const TABS = [
  { id: 'overview', label: 'Overview', icon: User },
  { id: 'measurements', label: 'Measurements', icon: Ruler },
  { id: 'profiling', label: 'Profiling', icon: ClipboardCheck },
  { id: 'medical', label: 'Medical', icon: Heart },
  { id: 'ai-coach', label: 'Coach Summary', icon: Brain },
  { id: 'nutrition', label: 'Nutrition', icon: Utensils },
  { id: 'trends', label: 'Trends', icon: BarChart3 },
  { id: 'documents', label: 'Media', icon: FolderOpen },
  { id: 'alerts', label: 'Alerts', icon: Bell }
];

export default function ChildProfile() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [child, setChild] = useState(null);
  const [showCoachSummary, setShowCoachSummary] = useState(false);
  const [measurements, setMeasurements] = useState([]);
  const [predictions, setPredictions] = useState(null);
  const [athleticTests, setAthleticTests] = useState([]);
  const [testSummary, setTestSummary] = useState(null);
  const [coachRatings, setCoachRatings] = useState([]);
  const [latestRating, setLatestRating] = useState(null);
  const [smhatAssessments, setSMHATAssessments] = useState([]);
  const [latestSMHAT, setLatestSMHAT] = useState(null);
  const [showComingSoon, setShowComingSoon] = useState(null);
  
  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };
  const [showAllTests, setShowAllTests] = useState(false);
  const tabsRef = useRef(null);
  
  // Google Meet state
  const [meetUrl, setMeetUrl] = useState(null);
  const [meetExpanded, setMeetExpanded] = useState(false);

  // Handle tab navigation from URL params (for OAuth callbacks)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && TABS.find(t => t.id === tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Swipe handlers for mobile
  const currentTabIndex = TABS.findIndex(t => t.id === activeTab);
  
  const goToNextTab = useCallback(() => {
    if (currentTabIndex < TABS.length - 1) {
      setActiveTab(TABS[currentTabIndex + 1].id);
    }
  }, [currentTabIndex]);

  const goToPrevTab = useCallback(() => {
    if (currentTabIndex > 0) {
      setActiveTab(TABS[currentTabIndex - 1].id);
    }
  }, [currentTabIndex]);

  const { onTouchStart, onTouchMove, onTouchEnd, swiping, swipeOffset } = useSwipe(
    goToNextTab,  // swipe left = next tab
    goToPrevTab,  // swipe right = prev tab
    50
  );

  const loadData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/children/${childId}/growth-data`);
      setChild(response.data.child);
      setMeasurements(response.data.measurements);
      setPredictions(response.data.latest_predictions);
      
      // Load athletic tests
      try {
        const testsResponse = await axios.get(`${API}/children/${childId}/athletic-tests`);
        setAthleticTests(testsResponse.data);
        
        const summaryResponse = await axios.get(`${API}/children/${childId}/athletic-tests/summary`);
        setTestSummary(summaryResponse.data);
      } catch (testError) {
        // No athletic tests yet - expected for new athletes
      }

      // Load coach ratings
      try {
        const ratingsResponse = await axios.get(`${API}/children/${childId}/coach-ratings`);
        setCoachRatings(ratingsResponse.data);
        
        if (ratingsResponse.data.length > 0) {
          setLatestRating(ratingsResponse.data[0]);
        }
      } catch (ratingError) {
        // No coach ratings yet - expected for new athletes
      }

      // Load SMHAT assessments
      try {
        const smhatResponse = await axios.get(`${API}/children/${childId}/smhat-assessments`);
        setSMHATAssessments(smhatResponse.data);
        
        if (smhatResponse.data.length > 0) {
          setLatestSMHAT(smhatResponse.data[0]);
        }
      } catch (smhatError) {
        // No SMHAT assessments yet - expected for new athletes
      }
    } catch (error) {
      toast.error('Failed to load child data');
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-zinc-500 font-mono animate-pulse" data-testid="loading-state">Loading...</div>
      </div>
    );
  }

  if (!child) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-zinc-500">Child not found</div>
      </div>
    );
  }

  const chartData = measurements.map((m) => ({
    date: new Date(m.measurement_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
    age: m.age_years,
    height: m.standing_height,
    weight: m.weight,
    heightPercentile: m.uk_who_percentiles?.height_percentile,
    weightPercentile: m.uk_who_percentiles?.weight_percentile
  }));

  return (
    <div className="min-h-screen bg-[#050505] grid-texture">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-950/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-zinc-400 hover:text-white mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              {/* Profile Photo */}
              <ProfilePhotoUpload 
                childId={childId}
                currentPhotoUrl={child.profile_photo_url}
                onPhotoUpdate={(newUrl) => setChild(prev => ({ ...prev, profile_photo_url: newUrl }))}
              />
              
              <div>
                <h1 className="text-4xl font-bold text-white uppercase tracking-tight" data-testid="child-name">
                  {child.first_name} {child.last_name}
                </h1>
                <div className="flex gap-6 mt-2 text-sm font-mono text-zinc-500">
                  <span>DOB: {new Date(child.date_of_birth).toLocaleDateString()}</span>
                  <span>Gender: {child.gender}</span>
                  <span>Age: {child.date_of_birth ? ((new Date() - new Date(child.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000)).toFixed(1) : '0'} years</span>
                </div>
              </div>
            </div>
            <div className="flex gap-4">
              <Button
                onClick={() => setShowCoachSummary(true)}
                variant="outline"
                className="h-12 px-5 border-[#02b5ff] text-[#02b5ff] hover:bg-[#02b5ff]/10 font-bold uppercase tracking-wider text-sm"
                data-testid="open-coach-summary-btn"
              >
                <ClipboardEdit className="mr-2 h-5 w-5" />
                Coach Summary
              </Button>
              <Button
                data-testid="download-report-button"
                onClick={async () => {
                  // Check if DOB is set
                  if (!child?.date_of_birth) {
                    toast.error('Date of birth must be set before generating the dossier. Please update the athlete profile.');
                    return;
                  }
                  const loadingToast = toast.loading('Building athlete dossier — performance profile, AI recommendations and 6-week microcycle (~30s)…', { duration: 60000 });
                  try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${API}/dossier/${childId}/generate`, {
                      method: 'POST',
                      headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({ email_athlete: true, use_cached_ai: true })
                    });
                    toast.dismiss(loadingToast);
                    if (response.ok) {
                      const blob = await response.blob();
                      const emailStatus = response.headers.get('X-Email-Status');
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `Athlete_Dossier_${child?.first_name}_${child?.last_name}.pdf`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                      if (emailStatus === 'sent') {
                        toast.success('Dossier saved to Documents, downloaded, and emailed to the athlete.');
                      } else {
                        toast.success('Dossier saved to Documents and downloaded. Athlete email skipped (no email on file).');
                      }
                    } else {
                      const error = await response.json().catch(() => ({}));
                      toast.error(error.detail || 'Failed to generate dossier');
                    }
                  } catch (error) {
                    toast.dismiss(loadingToast);
                    toast.error('Failed to generate dossier');
                  }
                }}
                variant="outline"
                className="h-12 px-6 border-[#ff025f] text-[#ff025f] hover:bg-[#ff025f]/10 font-bold uppercase tracking-wider text-sm"
              >
                <FileText className="mr-2 h-5 w-5" />
                Generate Athlete Dossier
              </Button>
              <Button
                data-testid="add-measurement-button"
                onClick={() => navigate(`/child/${childId}/add-measurement`)}
                className="h-12 px-8 bg-[#02b5ff] hover:bg-[#02b5ff] text-white font-bold uppercase tracking-wider text-sm hover:shadow-[0_0_30px_rgba(37,99,235,0.4)]"
              >
                <Plus className="mr-2 h-5 w-5" />
                Add Measurement
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-zinc-900/50 border-y border-zinc-800 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center">
            {/* Previous tab indicator (mobile) */}
            <button
              onClick={goToPrevTab}
              disabled={currentTabIndex === 0}
              className={`md:hidden p-2 ${currentTabIndex === 0 ? 'text-zinc-700' : 'text-zinc-400 hover:text-white'}`}
              aria-label="Previous tab"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            
            {/* Tabs */}
            <div 
              ref={tabsRef}
              className="flex gap-1 overflow-x-auto flex-1 scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {TABS.map((tab, index) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabClick(tab.id)}
                    className={`px-4 md:px-6 py-4 text-sm font-medium uppercase tracking-wider transition-all border-b-2 whitespace-nowrap flex items-center gap-2 ${
                      activeTab === tab.id 
                        ? 'text-[#02b5ff] border-[#02b5ff] bg-[#02b5ff]/5' 
                        : 'text-zinc-500 border-transparent hover:text-zinc-300 hover:bg-zinc-800/30'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                );
              })}
            </div>
            
            {/* Next tab indicator (mobile) */}
            <button
              onClick={goToNextTab}
              disabled={currentTabIndex === TABS.length - 1}
              className={`md:hidden p-2 ${currentTabIndex === TABS.length - 1 ? 'text-zinc-700' : 'text-zinc-400 hover:text-white'}`}
              aria-label="Next tab"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          
          {/* Swipe indicator dots (mobile) */}
          <div className="flex justify-center gap-1.5 py-2 md:hidden">
            {TABS.map((tab, index) => (
              <div
                key={tab.id}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  index === currentTabIndex 
                    ? 'bg-[#02b5ff] w-4' 
                    : 'bg-zinc-700'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div 
        className="max-w-7xl mx-auto px-6 py-12"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          transform: swiping ? `translateX(${swipeOffset * 0.3}px)` : 'translateX(0)',
          transition: swiping ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        {measurements.length === 0 && activeTab === 'overview' ? (
          <div className="space-y-6" data-testid="no-measurements">
            {/* 1. Profile Details - Always first */}
            <InlineProfileEditor 
              child={child}
              onUpdate={(data) => setChild(prev => ({ ...prev, ...data }))}
            />
            
            {/* 2. Current Team */}
            <TeamSelection 
              childId={childId}
              childName={child ? `${child.first_name} ${child.last_name}` : ''}
              onTeamAssigned={(teamName) => setChild(prev => ({ ...prev, current_team_name: teamName }))}
            />
            
            {/* 3. Season Targets */}
            <PlayerTargets 
              childId={childId}
              childName={child ? `${child.first_name} ${child.last_name}` : ''}
            />
            
            {/* 4. Add First Measurement - Prominent CTA */}
            <div className="bg-zinc-950/30 border border-zinc-800 rounded-xl p-8 text-center">
              <Activity className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2 uppercase">No Measurements Yet</h3>
              <p className="text-zinc-500 mb-4">Add the first measurement to start tracking growth</p>
              <Button
                data-testid="empty-add-measurement-button"
                onClick={() => navigate(`/child/${childId}/add-measurement`)}
                className="h-12 px-8 bg-[#02b5ff] hover:bg-[#02b5ff] text-white font-bold uppercase tracking-wider text-sm"
              >
                <Plus className="mr-2 h-5 w-5" />
                Add First Measurement
              </Button>
            </div>
            
            {/* 5. Performance Test Results - Show if any tests exist */}
            {athleticTests.length > 0 && (
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Activity className="h-5 w-5 text-[#02b5ff]" />
                        Performance Test Results ({athleticTests.length} tests)
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          testSummary?.overall_average_grade >= 4 ? 'bg-green-500/20 text-green-400' :
                          testSummary?.overall_average_grade >= 3 ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          Overall: {testSummary?.overall_average_grade?.toFixed(1)}/5
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowAllTests(!showAllTests)}
                          className="border-zinc-700 text-zinc-400 hover:text-white"
                          data-testid="toggle-all-tests-btn"
                        >
                          {showAllTests ? 'Show Less' : 'View All Details'}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Summary Grid - Always Show */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {testSummary?.test_summary?.slice(0, showAllTests ? undefined : 8).map((test) => (
                        <div 
                          key={test.test_name} 
                          className="bg-zinc-800/50 rounded-lg p-3 hover:bg-zinc-800/70 transition-colors cursor-pointer group"
                          onClick={() => {
                            // Scroll to analytics section when test is clicked
                            const analyticsSection = document.querySelector('[data-testid="athletic-analytics"]');
                            if (analyticsSection) {
                              analyticsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              // Determine which tab to activate based on test name
                              const testName = test.test_name.toLowerCase();
                              const sprintTab = document.querySelector('[data-testid="analytics-tab-sprint"]');
                              const balanceTab = document.querySelector('[data-testid="analytics-tab-balance"]');
                              setTimeout(() => {
                                if (testName.includes('sprint') || testName.includes('speed') || testName.includes('30m') || testName.includes('20m') || testName.includes('10m')) {
                                  sprintTab?.click();
                                } else if (testName.includes('balance') || testName.includes('5-0-5') || testName.includes('505') || testName.includes('leg') || testName.includes('agility')) {
                                  balanceTab?.click();
                                }
                              }, 500);
                            }
                          }}
                        >
                          <p className="text-xs text-zinc-500 mb-1 truncate group-hover:text-[#02b5ff] transition-colors" title={test.test_name}>{test.test_name}</p>
                          <p className="text-lg font-bold text-white">
                            {test.latest_value?.toFixed(2) || 'N/A'}
                            <span className="text-xs text-zinc-500 ml-1">{test.unit || ''}</span>
                          </p>
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-zinc-400">{test.count} test{test.count !== 1 ? 's' : ''}</p>
                            {test.latest_grade && (
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                test.latest_grade >= 4 ? 'bg-green-500/20 text-green-400' :
                                test.latest_grade >= 3 ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                                Grade: {test.latest_grade}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-[#02b5ff] opacity-0 group-hover:opacity-100 transition-opacity mt-1">Click to view graphs →</p>
                        </div>
                      ))}
                    </div>
                    
                    {!showAllTests && testSummary?.test_summary?.length > 8 && (
                      <button 
                        onClick={() => setShowAllTests(true)}
                        className="w-full mt-4 py-2 text-sm text-[#02b5ff] hover:text-white transition-colors"
                      >
                        +{testSummary.test_summary.length - 8} more test types — Click to expand
                      </button>
                    )}
                  </div>
                  
                  {/* Detailed Test History - Show when expanded */}
                  {showAllTests && (
                    <div className="border-t border-zinc-800 p-6 bg-zinc-900/30">
                      <h4 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">
                        Full Test History (Most Recent First)
                      </h4>
                      <div className="max-h-[400px] overflow-y-auto space-y-2">
                        {athleticTests.slice(0, 50).map((test, idx) => (
                          <div 
                            key={test.id || idx} 
                            className="flex items-center justify-between p-3 bg-zinc-800/40 rounded-lg hover:bg-zinc-800/60 transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-white truncate">{test.test_name}</p>
                              <p className="text-xs text-zinc-500">
                                {test.test_date ? new Date(test.test_date).toLocaleDateString() : 'No date'}
                                {test.session_name && ` • ${test.session_name}`}
                              </p>
                            </div>
                            <div className="flex items-center gap-4 flex-shrink-0">
                              <div className="text-right">
                                <p className="font-bold text-white">
                                  {test.result_value?.toFixed(2) || 'N/A'}
                                  <span className="text-xs text-zinc-500 ml-1">{test.unit || ''}</span>
                                </p>
                                {test.percentile && (
                                  <p className="text-xs text-zinc-400">{test.percentile}th percentile</p>
                                )}
                              </div>
                              {test.grade && (
                                <span className={`px-2 py-1 rounded text-sm font-bold ${
                                  test.grade >= 4 ? 'bg-green-500/20 text-green-400' :
                                  test.grade >= 3 ? 'bg-yellow-500/20 text-yellow-400' :
                                  'bg-red-500/20 text-red-400'
                                }`}>
                                  {test.grade}/5
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                        {athleticTests.length > 50 && (
                          <p className="text-center text-xs text-zinc-500 py-2">
                            Showing first 50 of {athleticTests.length} tests
                          </p>
                        )}
                      </div>
                      
                      {/* Test Comments Section */}
                      {athleticTests.some(t => t.comments || t.notes) && (
                        <div className="mt-4 pt-4 border-t border-zinc-800">
                          <h5 className="text-sm font-semibold text-zinc-400 mb-2">Test Comments</h5>
                          <div className="space-y-2">
                            {athleticTests.filter(t => t.comments || t.notes).slice(0, 10).map((test, idx) => (
                              <div key={idx} className="p-2 bg-zinc-800/30 rounded text-sm">
                                <span className="text-[#02b5ff]">{test.test_name}:</span>{' '}
                                <span className="text-zinc-300">{test.comments || test.notes}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* 6. Performance Analytics - Sprint & Balance Graphs */}
              {athleticTests.length > 0 && (
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden p-6">
                  <AthleticAnalytics 
                    childId={childId} 
                    childName={`${child?.first_name} ${child?.last_name}`} 
                  />
                </div>
              )}
              
              {/* 7. Consent Status */}
              <ConsentStatus 
                childId={childId} 
                childName={child ? `${child.first_name} ${child.last_name}` : ''} 
                childEmail={child?.email}
                parentEmail={child?.parent_email}
              />
              
              {/* 8. Upcoming Bookings */}
              <UpcomingBookings 
                childId={childId}
                onJoinMeet={(url) => setMeetUrl(url)}
              />
          </div>
        ) : (
          <>
            {/* Overview Tab - Reorganized Order */}
            {activeTab === 'overview' && (
              <OverviewTab
                childId={childId}
                child={child}
                onChildUpdate={(data) => setChild(prev => ({ ...prev, ...data }))}
                measurements={measurements}
                athleticTests={athleticTests}
                testSummary={testSummary}
                onJoinMeet={(url) => setMeetUrl(url)}
              />
            )}

            {/* Measurements Tab - Growth Charts */}
            {activeTab === 'measurements' && (
              <MeasurementsTab
                childId={childId}
                child={child}
                measurements={measurements}
                predictions={predictions}
                athleticTests={athleticTests}
                testSummary={testSummary}
                coachRatings={coachRatings}
                latestRating={latestRating}
                smhatAssessments={smhatAssessments}
                latestSMHAT={latestSMHAT}
              />
            )}

            {/* Medical Tab */}
            {activeTab === 'medical' && (
              <MedicalTab 
                childId={childId}
                childName={child ? `${child.first_name} ${child.last_name}` : ''}
              />
            )}

            {/* Profiling Tab */}
            {activeTab === 'profiling' && (
              <ProfilingTab 
                childId={childId}
                childName={child ? `${child.first_name} ${child.last_name}` : ''}
                user={user}
              />
            )}

            {/* AI Coach Tab */}
            {activeTab === 'ai-coach' && (
              <AICoachTab 
                childId={childId}
                childName={child ? `${child.first_name} ${child.last_name}` : ''}
              />
            )}

            {/* Nutrition Tab */}
            {activeTab === 'nutrition' && (
              <NutritionTab 
                childId={childId}
                childName={child ? `${child.first_name} ${child.last_name}` : ''}
              />
            )}

            {/* Performance Trends Tab - includes Fitness Metrics */}
            {activeTab === 'trends' && (
              <TrendsTab 
                childId={childId}
                childName={child ? `${child.first_name} ${child.last_name}` : ''}
                childData={child}
                measurements={measurements}
                athleticTests={athleticTests}
                coachRatings={coachRatings}
              />
            )}

            {/* Documents & Videos Tab */}
            {activeTab === 'documents' && (
              <MediaTab 
                childId={childId}
                childName={child ? `${child.first_name} ${child.last_name}` : ''}
              />
            )}

            {/* Alerts Tab */}
            {activeTab === 'alerts' && (
              <AlertsTab childId={childId} />
            )}
          </>
        )}
      </div>

      {/* Google Meet Embedded Window */}
      {meetUrl && (
        <div className={`fixed ${meetExpanded ? 'inset-4' : 'bottom-4 right-4 w-[500px] h-[400px]'} bg-zinc-900 rounded-xl border border-zinc-700 shadow-2xl z-50 overflow-hidden transition-all`} data-testid="meet-window">
          <div className="flex items-center justify-between p-3 bg-zinc-800 border-b border-zinc-700">
            <div className="flex items-center gap-2">
              <Video className="h-4 w-4 text-green-400" />
              <span className="text-sm font-medium text-white">Google Meet</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMeetExpanded(!meetExpanded)}
                className="p-1.5 hover:bg-zinc-700 rounded text-zinc-400 hover:text-white"
                title={meetExpanded ? 'Minimize' : 'Maximize'}
              >
                {meetExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </button>
              <a
                href={meetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 hover:bg-zinc-700 rounded text-zinc-400 hover:text-white"
                title="Open in new tab"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
              <button
                onClick={() => {
                  setMeetUrl(null);
                  setMeetExpanded(false);
                }}
                className="p-1.5 hover:bg-red-500/20 rounded text-zinc-400 hover:text-red-400"
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <iframe
            src={meetUrl}
            width="100%"
            height={meetExpanded ? 'calc(100% - 48px)' : '352'}
            frameBorder="0"
            allow="camera; microphone; fullscreen; display-capture; autoplay"
            className="bg-black"
            title="Google Meet"
          />
        </div>
      )}

      {/* Coach Summary editor modal */}
      <CoachSummaryEditor
        childId={childId}
        childName={child ? `${child.first_name} ${child.last_name}` : ''}
        open={showCoachSummary}
        onClose={() => setShowCoachSummary(false)}
        onSaved={() => { /* could trigger UI refresh — dossier will pick up latest */ }}
      />
    </div>
  );
}